import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  CalendarClock,
  Download,
  Eye,
  FileText,
  Info,
  Pencil,
  UserRound,
} from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet, type Onglet } from '../components/ui/Onglets';
import { Modale } from '../components/ui/Modale';
import { Icone } from '../components/Icone';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { urlCompteRenduImmersion } from '../lib/useMatching';
import { ModaleOrganisation } from '../components/repertoire/ModaleOrganisation';
import { ModaleRecueilBesoin } from '../components/commercial/ModaleRecueilBesoin';
import { DetailBesoin } from '../components/commercial/DetailBesoin';
import { useToast } from '../components/ui/useToast';
import { useAuth } from '../auth/useAuth';
import { useSites } from '../lib/useSites';
import { useTaillePage } from '../lib/useTaillePage';
import { urlDocumentGenere } from '../lib/useDocuments';
import { mentionRupture } from '../lib/useContrats';
import { PASTILLE_DOCUMENT } from '../lib/pastilles';
import { ErreurApi } from '../lib/api';
import { peutTenirLeRepertoire } from '../lib/droits-repertoire';
import { useEnregistrerBesoin, useModifierEntreprise } from '../lib/useEntreprises';
import {
  useDossierRepertoire,
  useModifierPartenaire,
  type ContratDossier,
  type DocumentDossierOrganisation,
  type FicheOrganisation,
  type ImmersionDossier,
  type TuteurDossier,
} from '../lib/usePedagogique';
import type { Besoin } from '../lib/useEntreprises';
import { periodePositionnement } from '../lib/immersion-statut';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function jour(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Normalisation d'une recherche : sans accent ni casse, comme on la tape. */
function normaliser(valeur: string): string {
  return valeur
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

type Section = 'besoins' | 'contrats' | 'immersions' | 'documents';

/**
 * Écran « Dossier d'une organisation » (`/ped-repertoire/:id`).
 *
 * Pendant du dossier apprenant, côté organisation : l'en-tête identifie,
 * les onglets suivent. Une entreprise et un partenaire ouvrent le même
 * dossier — un partenaire n'accueille pas d'apprenti, ses sections sont donc
 * vides et le disent, plutôt que de disparaître et laisser croire que
 * l'information est ailleurs.
 *
 * La séparation est volontaire : la **fiche info** porte l'identité de
 * l'organisation, le **dossier** porte son suivi. Mêler les deux redonnerait
 * la page-catalogue qu'on remplace.
 *
 * Aucune section n'affiche sa liste entière : chacune a sa recherche, ses
 * filtres et sa pagination, dès maintenant — une entreprise partenaire de
 * longue date finit avec des dizaines de contrats et de documents. C'est la
 * pagination qui dit combien d'éléments sont affichés ; l'en-tête de `Carte`
 * est en `overflow-hidden` et coupait ce compteur sans prévenir en mobile.
 */
export function RepertoireDossierPage(): JSX.Element {
  const { id } = useParams();
  const { utilisateur, peutAcceder } = useAuth();
  const toast = useToast();
  const client = useQueryClient();
  const { data, isLoading, error } = useDossierRepertoire(id);
  const { data: tousLesSites } = useSites();
  const modifierEntreprise = useModifierEntreprise();
  const modifierPartenaire = useModifierPartenaire();
  const enregistrerBesoin = useEnregistrerBesoin();

  const [section, setSection] = useState<Section>('besoins');
  const [ficheOuverte, setFicheOuverte] = useState(false);
  const [editionOuverte, setEditionOuverte] = useState(false);
  const [recueilOuvert, setRecueilOuvert] = useState(false);
  // Compte rendu consulté sans quitter le dossier : même visionneuse que le
  // dossier apprenant et que l'écran des périodes d'immersion.
  const [compteRenduVu, setCompteRenduVu] = useState<ImmersionDossier | null>(null);
  // Un document réglementaire se lit avant d'être téléchargé : la visionneuse
  // est celle du dossier apprenant, et le fichier passe par la même route
  // gardée — rien n'est servi en statique.
  const [documentVu, setDocumentVu] = useState<DocumentDossierOrganisation | null>(null);
  // Un besoin se lit en entier — profil, tâches, diffusion, candidat — avant
  // d'être suivi : la liste n'en montre que l'essentiel. Même fiche que dans
  // « com-entreprises », en lecture seule ici : la correction reste l'acte de
  // qui prospecte.
  const [besoinVu, setBesoinVu] = useState<Besoin | null>(null);

  // Les droits viennent de l'API : le front applique la liste reçue, il ne
  // rejoue pas la matrice. La tenue du répertoire — créer et corriger un
  // partenaire — est en revanche une affaire de profil, comme côté serveur.
  const prospection = peutAcceder('com-entreprises');
  const tientLeRepertoire = peutTenirLeRepertoire(utilisateur?.profil);
  // Le fichier d'un document réglementaire passe par une route gardée : sans le
  // droit, la ligne s'affiche sans le lien plutôt que de servir un 403 au clic.
  const telechargement = peutAcceder('adm-documents') || peutAcceder('adm-dossier');
  const versDossierApprenant = peutAcceder('adm-dossier') || peutAcceder('learners');

  /** Le dossier vit sous la clé `repertoire` ; les mutations d'entreprise non. */
  const rafraichir = (): void => {
    void client.invalidateQueries({ queryKey: ['repertoire'] });
  };

  if (isLoading) return <Chargement libelle="Chargement du dossier…" />;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1500px] space-y-4 p-4 sm:p-6">
        <RetourRepertoire />
        <MessageErreur
          message={
            error instanceof ErreurApi
              ? error.message
              : 'Le dossier de cette organisation n’a pas pu être chargé.'
          }
        />
      </div>
    );
  }

  const { organisation, compteurs, referentiels } = data;
  const entreprise = organisation.genre === 'ENTREPRISE';
  const modifiable = entreprise ? prospection : tientLeRepertoire;
  const enCoursModification = entreprise
    ? modifierEntreprise.isPending
    : modifierPartenaire.isPending;
  const erreurModification = entreprise ? modifierEntreprise.error : modifierPartenaire.error;

  const onglets: Onglet<Section>[] = [
    { cle: 'besoins', libelle: 'Besoins', compte: compteurs.besoinsTotal },
    { cle: 'contrats', libelle: 'Contrats d’apprentissage', compte: compteurs.contratsTotal },
    { cle: 'immersions', libelle: 'Périodes d’immersion', compte: compteurs.immersions },
    { cle: 'documents', libelle: 'Documents', compte: compteurs.documents },
  ];

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 p-4 sm:p-6">
      <RetourRepertoire />

      <EnteteOrganisation
        organisation={organisation}
        modifiable={modifiable}
        onFiche={() => setFicheOuverte(true)}
        onModifier={() => {
          modifierEntreprise.reset();
          modifierPartenaire.reset();
          setEditionOuverte(true);
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Postes ouverts"
          valeur={compteurs.postesOuverts}
          detail={`${compteurs.besoinsOuverts} besoin${
            compteurs.besoinsOuverts > 1 ? 's' : ''
          } en cours`}
          ton="mint"
        />
        <Compteur
          libelle="Contrats en cours"
          valeur={compteurs.contratsEnCours}
          detail={`${compteurs.contratsTotal} au total`}
          ton="navy"
        />
        <Compteur
          libelle="Immersions"
          valeur={compteurs.immersions}
          detail="périodes en entreprise"
          ton="blue"
        />
        <Compteur
          libelle="Documents"
          valeur={compteurs.documents}
          detail="liés aux contrats"
          ton="warm"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2 min-w-0">
          {section === 'besoins' && (
            <SectionBesoins
              besoins={data.besoins}
              onglets={onglets}
              onSection={setSection}
              /* Le recueil du besoin est l'acte de qui prospecte : la garde est
                 la même que côté API (`com-entreprises`). Sans objet pour un
                 partenaire, qui n'accueille pas d'apprenti. */
              ajoutPossible={entreprise && prospection}
              onAjouter={() => {
                enregistrerBesoin.reset();
                setRecueilOuvert(true);
              }}
              partenaire={!entreprise}
              onVoir={setBesoinVu}
            />
          )}
          {section === 'contrats' && (
            <SectionContrats
              contrats={data.contrats}
              onglets={onglets}
              onSection={setSection}
              versDossier={versDossierApprenant}
              partenaire={!entreprise}
            />
          )}
          {section === 'immersions' && (
            <SectionImmersions
              immersions={data.immersions}
              onglets={onglets}
              onSection={setSection}
              versDossier={versDossierApprenant}
              partenaire={!entreprise}
              onVoirCompteRendu={setCompteRenduVu}
            />
          )}
          {section === 'documents' && (
            <SectionDocuments
              documents={data.documents}
              onglets={onglets}
              onSection={setSection}
              telechargeable={telechargement}
              partenaire={!entreprise}
              onVoir={setDocumentVu}
            />
          )}
        </div>

        <div className="space-y-5">
          <RelationEtContact organisation={organisation} />
          {entreprise && (
            <MaitresApprentissage tuteurs={data.tuteurs} versDossier={versDossierApprenant} />
          )}
        </div>
      </div>

      <ModaleFicheInfo
        organisation={ficheOuverte ? organisation : null}
        onFermer={() => setFicheOuverte(false)}
      />

      {editionOuverte && (
        <ModaleOrganisation
          key={organisation.id}
          ouverte
          fiche={organisation}
          maitresExistants={data.tuteurs}
          statutsRelation={referentiels.statutsRelation}
          typesPartenaire={referentiels.typesPartenaire}
          secteurs={referentiels.secteursSuggeres}
          typesEmployeur={referentiels.typesEmployeurSuggeres}
          employeursSpecifiques={referentiels.employeursSpecifiquesSuggeres}
          sitesDisponibles={(tousLesSites?.sites ?? []).map((entree) => entree.code)}
          enCours={enCoursModification}
          erreur={erreurModification instanceof ErreurApi ? erreurModification.message : null}
          onFermer={() => {
            modifierEntreprise.reset();
            modifierPartenaire.reset();
            setEditionOuverte(false);
          }}
          onEnregistrer={(sortie) => {
            const fini = {
              onSuccess: () => {
                toast('Informations mises à jour');
                setEditionOuverte(false);
                rafraichir();
              },
            };
            if (sortie.genre === 'ENTREPRISE' && sortie.entreprise) {
              modifierEntreprise.mutate({ id: organisation.id, ...sortie.entreprise }, fini);
            } else if (sortie.partenaire) {
              modifierPartenaire.mutate({ id: organisation.id, ...sortie.partenaire }, fini);
            }
          }}
        />
      )}

      <ModaleRecueilBesoin
        employeur={
          recueilOuvert && organisation.siret
            ? {
                id: organisation.id,
                denomination: organisation.nom,
                siret: organisation.siret,
                telephone: organisation.telephone,
                email: organisation.email,
              }
            : null
        }
        formations={referentiels.formations}
        typesContrat={referentiels.typesContrat}
        enCours={enregistrerBesoin.isPending}
        erreur={
          enregistrerBesoin.error instanceof ErreurApi ? enregistrerBesoin.error.message : null
        }
        // Le matching se lance depuis son propre écran : proposer ici un bouton
        // qui déplace la personne hors du dossier qu'elle est en train de lire
        // n'aiderait pas.
        avecMatching={false}
        onFermer={() => {
          enregistrerBesoin.reset();
          setRecueilOuvert(false);
        }}
        onRecueillir={(entree) =>
          enregistrerBesoin.mutate(
            { entrepriseId: organisation.id, ...entree },
            {
              onSuccess: () => {
                toast('Besoin enregistré');
                setRecueilOuvert(false);
                rafraichir();
              },
            },
          )
        }
      />

      {besoinVu && (
        <Modale
          ouverte
          onFermer={() => setBesoinVu(null)}
          titre={`Besoin · ${besoinVu.nombrePostes} poste${besoinVu.nombrePostes > 1 ? 's' : ''}`}
          sousTitre={besoinVu.formation ?? 'Formation à définir'}
        >
          <div className="p-4 sm:p-6">
            <DetailBesoin besoin={besoinVu} />
          </div>
        </Modale>
      )}

      {documentVu && (
        <VisionneusePiece
          ouverte
          libellePiece={documentVu.intitule}
          nomFichier={`${documentVu.intitule.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`}
          urlBase={urlDocumentGenere(documentVu.id)}
          onFermer={() => setDocumentVu(null)}
        />
      )}

      {compteRenduVu && (
        <VisionneusePiece
          ouverte
          libellePiece="Compte rendu d’entretien en entreprise"
          nomFichier={`compte-rendu-entretien-entreprise-${compteRenduVu.id}.pdf`}
          urlBase={urlCompteRenduImmersion(compteRenduVu.id)}
          onFermer={() => setCompteRenduVu(null)}
        />
      )}
    </div>
  );
}

function RetourRepertoire(): JSX.Element {
  return (
    <Link
      to="/ped-repertoire"
      className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-codialis-sky transition hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Répertoire entreprises &amp; partenaires
    </Link>
  );
}

function EnteteOrganisation({
  organisation,
  modifiable,
  onFiche,
  onModifier,
}: {
  organisation: FicheOrganisation;
  modifiable: boolean;
  onFiche: () => void;
  onModifier: () => void;
}): JSX.Element {
  const identifiant = organisation.siret ?? organisation.rna;

  return (
    <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-card">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-codialis-sand text-codialis-navy">
            <Building2 className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold text-codialis-navy">
              {organisation.nom}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-codialis-gray">
              {identifiant && <span className="font-mono">{identifiant}</span>}
              {organisation.activite && <span>{organisation.activite}</span>}
              {organisation.ville && <span>{organisation.ville}</span>}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Pastille texte={organisation.typeLibelle} ton={organisation.typeTon} />
              {organisation.relanceAttendue && (
                <Pastille texte="Relance attendue" ton="warning" />
              )}
              {!organisation.actif && <Pastille texte="Fiche archivée" ton="neutral" />}
              {organisation.sites.map((code) => (
                <PastilleSite key={code} site={code} />
              ))}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <BoutonSecondaire onClick={onFiche}>
            <span className="inline-flex items-center gap-1.5">
              <Info className="h-4 w-4" aria-hidden="true" />
              Voir la fiche info
            </span>
          </BoutonSecondaire>
          {modifiable && (
            <button
              type="button"
              onClick={onModifier}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Modifier les informations
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Une donnée de la fiche : l'intitulé au-dessus, la valeur en dessous. */
function Donnee({
  libelle,
  valeur,
  lien,
}: {
  libelle: string;
  valeur: string | number | null;
  lien?: string | null;
}): JSX.Element {
  const vide = valeur === null || valeur === '';
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
        {libelle}
      </div>
      <div className="mt-0.5 break-words text-sm text-codialis-navy">
        {vide ? (
          <span className="text-codialis-gray">—</span>
        ) : lien ? (
          <a href={lien} target="_blank" rel="noreferrer" className="text-codialis-sky hover:underline">
            {valeur}
          </a>
        ) : (
          valeur
        )}
      </div>
    </div>
  );
}

/**
 * Fiche info : l'identité de l'organisation, séparée de son suivi.
 *
 * Elle est en fenêtre plutôt qu'en carte permanente : on la consulte pour
 * vérifier une adresse ou un numéro, on ne la relit pas à chaque visite du
 * dossier.
 */
function ModaleFicheInfo({
  organisation,
  onFermer,
}: {
  organisation: FicheOrganisation | null;
  onFermer: () => void;
}): JSX.Element | null {
  if (!organisation) return null;

  const entreprise = organisation.genre === 'ENTREPRISE';
  const adresse = [
    organisation.adresse,
    [organisation.codePostal, organisation.ville].filter(Boolean).join(' '),
  ]
    .filter((ligne) => ligne && ligne.trim() !== '')
    .join(', ');

  const siteWeb = organisation.siteWeb;
  const lienSite = siteWeb ? (/^https?:\/\//i.test(siteWeb) ? siteWeb : `https://${siteWeb}`) : null;

  return (
    <Modale
      ouverte
      titre="Fiche info"
      sousTitre={organisation.nom}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-6 p-4 sm:p-6">
        <BlocFiche titre="Identification">
          <Donnee libelle={entreprise ? 'Raison sociale' : 'Nom'} valeur={organisation.nom} />
          <Donnee libelle="Nature" valeur={organisation.typeLibelle} />
          {entreprise ? (
            <>
              <Donnee libelle="SIRET" valeur={organisation.siret} />
              <Donnee libelle="SIREN" valeur={organisation.siren} />
              <Donnee libelle="Code NAF" valeur={organisation.codeNaf} />
            </>
          ) : (
            <Donnee libelle="Numéro RNA" valeur={organisation.rna} />
          )}
          <Donnee
            libelle={entreprise ? 'Secteur d’activité' : 'Domaine d’intervention'}
            valeur={organisation.activite}
          />
          <Donnee libelle="Site internet" valeur={siteWeb} lien={lienSite} />
        </BlocFiche>

        <BlocFiche titre="Coordonnées">
          <div className="sm:col-span-2 min-w-0">
            <Donnee libelle="Adresse" valeur={adresse === '' ? null : adresse} />
          </div>
          {entreprise && (
            <>
              <Donnee libelle="N° de voie" valeur={organisation.numeroVoie} />
              <Donnee libelle="Voie" valeur={organisation.voie} />
              <Donnee libelle="Complément d’adresse" valeur={organisation.complementAdresse} />
              <Donnee libelle="Code postal" valeur={organisation.codePostal} />
            </>
          )}
          <Donnee libelle="Téléphone" valeur={organisation.telephone} />
          <Donnee libelle="Adresse e-mail" valeur={organisation.email} />
          <Donnee libelle="Personne de contact" valeur={organisation.contactOperationnel} />
          {entreprise && <Donnee libelle="Gérant" valeur={organisation.gerant} />}
        </BlocFiche>

        {entreprise && (
          <BlocFiche titre="Employeur">
            <Donnee
              libelle="Employeur privé ou public"
              valeur={
                organisation.secteurEmployeur === null
                  ? null
                  : organisation.secteurEmployeur === 'PUBLIC'
                    ? 'Employeur public'
                    : 'Employeur privé'
              }
            />
            <Donnee libelle="Type d’employeur" valeur={organisation.typeEmployeur} />
            <Donnee libelle="Employeur spécifique" valeur={organisation.employeurSpecifique} />
            <Donnee libelle="Effectif total salariés" valeur={organisation.effectif} />
            <Donnee libelle="OPCO" valeur={organisation.opco} />
            <Donnee libelle="Convention collective (IDCC)" valeur={organisation.idcc} />
            <Donnee libelle="Caisse de retraite complémentaire" valeur={organisation.caisseRetraite} />
            {organisation.secteurEmployeur === 'PUBLIC' && (
              <div className="sm:col-span-2 min-w-0">
                <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  Employeur public : l’adhésion de l’apprenti(e) au régime spécifique
                  d’assurance chômage est à prévoir au contrat.
                </p>
              </div>
            )}
          </BlocFiche>
        )}

        <BlocFiche titre="Suivi">
          <Donnee libelle="Antennes de rattachement" valeur={organisation.sites.join(', ')} />
          <Donnee libelle="Dernier échange" valeur={jour(organisation.dernierContactLe)} />
          <Donnee libelle="Fiche créée le" valeur={jour(organisation.creeLe)} />
          <Donnee libelle="Dernière mise à jour" valeur={jour(organisation.modifieLe)} />
        </BlocFiche>
      </div>
    </Modale>
  );
}

function BlocFiche({
  titre,
  children,
}: {
  titre: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {titre}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function RelationEtContact({
  organisation,
}: {
  organisation: FicheOrganisation;
}): JSX.Element {
  return (
    <Carte titre="Relation" sansBordure>
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={organisation.typeLibelle} ton={organisation.typeTon} />
          {organisation.relanceAttendue && <Pastille texte="Relance attendue" ton="warning" />}
        </div>
        {organisation.genre === 'ENTREPRISE' && (
          <Donnee libelle="Dernier échange" valeur={jour(organisation.dernierContactLe)} />
        )}
        <Donnee libelle="Interlocuteur" valeur={organisation.contactOperationnel} />
        <Donnee libelle="Téléphone" valeur={organisation.telephone} />
        <Donnee libelle="Adresse e-mail" valeur={organisation.email} />
        <Donnee libelle="Antennes" valeur={organisation.sites.join(', ')} />
      </div>
    </Carte>
  );
}

/** `AAAA-MM-JJ` → `JJ/MM/AAAA`, sans passer par un instant ni un fuseau. */
function jourCivil(iso: string): string {
  const [annee, mois, jourDuMois] = iso.split('-');
  return `${jourDuMois}/${mois}/${annee}`;
}

function apprentisSuivis(nombre: number): string {
  return nombre === 0 ? 'Aucun apprenti suivi' : `${nombre} apprenti${nombre > 1 ? 's' : ''} suivi${nombre > 1 ? 's' : ''}`;
}

/**
 * Maîtres d'apprentissage mémorisés de l'entreprise.
 *
 * Chacun s'ouvre sur sa fiche, avec l'historique de ses contrats (RG3 du
 * ticket du 13/10/2026). Le nombre d'apprentis suivis est celui du jour,
 * calculé par l'API depuis les dates : un contrat terminé libère le maître le
 * lendemain, une rupture à sa date.
 */
function MaitresApprentissage({
  tuteurs,
  versDossier,
}: {
  tuteurs: TuteurDossier[];
  versDossier: boolean;
}): JSX.Element {
  const [ouvert, setOuvert] = useState<string | null>(null);
  const fiche = tuteurs.find((tuteur) => tuteur.id === ouvert) ?? null;

  return (
    <Carte
      titre="Maîtres d’apprentissage"
      actions={<span className="text-xs text-codialis-gray">{tuteurs.length}</span>}
      sansBordure
    >
      {tuteurs.length === 0 ? (
        <Vide
          icone={<UserRound className="h-5 w-5" aria-hidden="true" />}
          texte="Aucun maître d’apprentissage mémorisé pour cette entreprise."
        />
      ) : (
        <ul className="divide-y divide-gray-100">
          {tuteurs.map((tuteur) => (
            <li key={tuteur.id}>
              <button
                type="button"
                onClick={() => setOuvert(tuteur.id)}
                className="block w-full space-y-1 px-4 py-4 text-left transition hover:bg-gray-50 sm:px-6"
                aria-label={`Ouvrir la fiche de ${tuteur.prenom} ${tuteur.nom}`}
              >
                <span className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-codialis-navy">
                    {tuteur.prenom} {tuteur.nom}
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {tuteur.aPlafond && <Pastille texte="2 apprentis suivis" ton="warning" />}
                    {tuteur.attestationEligibilite && (
                      <Pastille texte="Éligibilité attestée" ton="success" />
                    )}
                  </span>
                </span>
                {tuteur.emploiOccupe && (
                  <span className="block text-xs text-codialis-gray">{tuteur.emploiOccupe}</span>
                )}
                <span className="block text-xs text-codialis-gray">
                  {[tuteur.telephone, tuteur.email].filter(Boolean).join(' · ') || '—'}
                </span>
                <span className="block text-[11px] text-codialis-gray">
                  {apprentisSuivis(tuteur.apprentisSuivis)} aujourd’hui · {tuteur.contrats} contrat
                  {tuteur.contrats > 1 ? 's' : ''} au total
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {fiche && (
        <FicheMaitreApprentissage
          tuteur={fiche}
          versDossier={versDossier}
          onFermer={() => setOuvert(null)}
        />
      )}
    </Carte>
  );
}

/** Fiche d'un maître d'apprentissage : identité, puis tous ses contrats. */
function FicheMaitreApprentissage({
  tuteur,
  versDossier,
  onFermer,
}: {
  tuteur: TuteurDossier;
  versDossier: boolean;
  onFermer: () => void;
}): JSX.Element {
  const experience =
    tuteur.anneesExperience === null
      ? null
      : `${tuteur.anneesExperience} an${tuteur.anneesExperience > 1 ? 's' : ''}`;

  return (
    <Modale
      ouverte
      titre={`${tuteur.prenom} ${tuteur.nom}`}
      sousTitre="Maître d’apprentissage"
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-6 p-4 sm:p-6">
        <BlocFiche titre="Identité">
          <Donnee libelle="Emploi occupé" valeur={tuteur.emploiOccupe} />
          <Donnee libelle="Expérience" valeur={experience} />
          <Donnee libelle="Diplôme le plus élevé" valeur={tuteur.diplomeLePlusEleve} />
          <Donnee libelle="Niveau du diplôme" valeur={tuteur.niveauDiplome} />
          <Donnee libelle="Téléphone" valeur={tuteur.telephone} />
          <Donnee libelle="Courriel" valeur={tuteur.email} />
          <Donnee
            libelle="Éligibilité"
            valeur={tuteur.attestationEligibilite ? 'Attestée par l’employeur' : 'Non attestée'}
          />
          <Donnee libelle="Apprentis suivis aujourd’hui" valeur={tuteur.apprentisSuivis} />
        </BlocFiche>

        {tuteur.aPlafond && (
          <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Ce maître d’apprentissage suit déjà {tuteur.apprentisSuivis} apprentis : le plafond
            légal de deux apprentis simultanés est atteint.
          </p>
        )}

        <section>
          <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Historique des contrats
          </h3>
          {tuteur.historique.length === 0 ? (
            <p className="text-sm text-codialis-gray">
              Aucun contrat ne désigne encore ce maître d’apprentissage.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
              {tuteur.historique.map((suivi) => (
                <li key={suivi.contratId} className="space-y-1 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-codialis-navy">
                      {versDossier ? (
                        <Link
                          to={`/adm-dossier/${suivi.apprenantId}`}
                          className="text-codialis-sky hover:underline"
                        >
                          {suivi.apprenant}
                        </Link>
                      ) : (
                        suivi.apprenant
                      )}
                    </span>
                    <Pastille texte={suivi.situation.libelle} ton={suivi.situation.ton} />
                  </div>
                  <div className="text-xs text-codialis-gray">{suivi.formation}</div>
                  <div className="text-xs text-codialis-gray">
                    {jour(suivi.dateDebut)} → {jour(suivi.dateFin)}
                    {suivi.situation.detail ? ` · ${suivi.situation.detail}` : ''}
                  </div>
                  <div className="text-[11px] font-semibold text-codialis-navy">
                    {!suivi.suiviJusquau
                      ? 'Non compté dans sa charge'
                      : suivi.suiviEnCours
                        ? `Suivi en cours, jusqu’au ${jourCivil(suivi.suiviJusquau)}`
                        : new Date(suivi.dateDebut).getTime() > Date.now()
                          ? `Suivi à partir du ${jour(suivi.dateDebut)}, jusqu’au ${jourCivil(suivi.suiviJusquau)}`
                          : `Suivi jusqu’au ${jourCivil(suivi.suiviJusquau)}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modale>
  );
}

/**
 * Recherche, filtre et pagination d'une section.
 *
 * Le même petit état pour les quatre : on filtre d'abord, on pagine ensuite, et
 * la page revient à 1 dès qu'un critère change — sinon on reste sur une page
 * qui n'existe plus.
 */
function useListeFiltree<T>(
  elements: T[],
  correspond: (element: T) => boolean,
  criteres: unknown[],
): {
  affiches: T[];
  total: number;
  pagination: JSX.Element | null;
} {
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const filtres = useMemo(
    () => elements.filter(correspond),
    // `correspond` est recréée à chaque rendu : ce sont les critères qui
    // comptent, pas l'identité de la fonction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [elements, ...criteres],
  );

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taillePage, ...criteres]);

  const total = filtres.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const affiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return filtres.slice(debut, debut + taillePage);
  }, [filtres, page, taillePage]);

  return {
    affiches,
    total,
    pagination:
      total > 0 ? (
        <Pagination
          page={page}
          pages={pages}
          total={total}
          affiches={affiches.length}
          taillePage={taillePage}
          onPage={setPage}
          onChangerTaille={setTaillePage}
        />
      ) : null,
  };
}

function SectionBesoins({
  besoins,
  onglets,
  onSection,
  ajoutPossible,
  onAjouter,
  partenaire,
  onVoir,
}: {
  besoins: Besoin[];
  onglets: Onglet<Section>[];
  onSection: (section: Section) => void;
  ajoutPossible: boolean;
  onAjouter: () => void;
  partenaire: boolean;
  /** Ouvre la fiche complète du besoin, sans quitter le dossier. */
  onVoir: (besoin: Besoin) => void;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');

  const terme = normaliser(recherche.trim());
  const { affiches, total, pagination } = useListeFiltree(
    besoins,
    (besoin) =>
      (statut === '' || besoin.statut === statut) &&
      (terme === '' ||
        normaliser(
          [besoin.formation, besoin.profilRecherche, besoin.commercial, besoin.candidatNom]
            .filter(Boolean)
            .join(' '),
        ).includes(terme)),
    [terme, statut],
  );

  return (
    <>
      <Onglets
        onglets={onglets}
        actif="besoins"
        onChanger={onSection}
        idPanneau="panneau-repertoire"
        actions={
          <>
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un besoin"
              placeholder="Formation, profil, commercial"
            />
            <ChampFiltre valeur={statut} onChange={setStatut} etiquette="Filtrer par statut">
              <option value="">Tous les statuts</option>
              <option value="OUVERT">Postes ouverts</option>
              <option value="POURVU">Pourvus</option>
              <option value="ABANDONNE">Abandonnés</option>
            </ChampFiltre>
            {ajoutPossible && (
              <BoutonPrincipal onClick={onAjouter}>+ Ajouter un besoin</BoutonPrincipal>
            )}
          </>
        }
      />

      <PanneauOnglet id="panneau-repertoire" actif="besoins" className="space-y-5">
        <Carte
          titre="Besoins de l’entreprise"
          sansBordure
        >
          {partenaire ? (
            <Vide
              icone={<Briefcase className="h-5 w-5" aria-hidden="true" />}
              texte="Un partenaire n’accueille pas d’apprenti : aucun besoin de recrutement n’est recueilli sur cette fiche."
            />
          ) : total === 0 ? (
            <Vide
              icone={<Briefcase className="h-5 w-5" aria-hidden="true" />}
              texte={
                besoins.length === 0
                  ? 'Aucun besoin recueilli pour cette entreprise.'
                  : 'Aucun besoin ne correspond à la recherche.'
              }
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {affiches.map((besoin) => (
                <li key={besoin.id} className="space-y-2 px-4 sm:px-6 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-codialis-navy">
                        {besoin.nombrePostes} poste{besoin.nombrePostes > 1 ? 's' : ''}
                        {besoin.formation ? ` · ${besoin.formation}` : ''}
                      </div>
                      <div className="mt-0.5 text-xs text-codialis-gray">
                        {besoin.typeContratLibelle ?? 'Type de contrat non précisé'} · recueilli le{' '}
                        {jour(besoin.dateDemande ?? besoin.creeLe)}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      {besoin.ouvertRqth && <Pastille texte="Ouvert RQTH" ton="info" />}
                      <Pastille texte={besoin.statutLibelle} ton={besoin.statutTon} />
                      {/* La liste résume ; la fiche dit tout — tâches confiées,
                          diffusion, candidat correspondant. */}
                      <button
                        type="button"
                        onClick={() => onVoir(besoin)}
                        className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-codialis-sky"
                        aria-label={`Voir le besoin de ${besoin.nombrePostes} poste${besoin.nombrePostes > 1 ? 's' : ''}${besoin.formation ? ` en ${besoin.formation}` : ''}`}
                      >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                        Voir
                      </button>
                    </div>
                  </div>
                  {besoin.profilRecherche && (
                    <p className="whitespace-pre-line text-sm text-codialis-gray">
                      {besoin.profilRecherche}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-codialis-gray">
                    {besoin.dateSouhaitee && (
                      <span>Entrée souhaitée : {jour(besoin.dateSouhaitee)}</span>
                    )}
                    {besoin.commercial && <span>Recueilli par {besoin.commercial}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {pagination}
        </Carte>
      </PanneauOnglet>
    </>
  );
}

function SectionContrats({
  contrats,
  onglets,
  onSection,
  versDossier,
  partenaire,
}: {
  contrats: ContratDossier[];
  onglets: Onglet<Section>[];
  onSection: (section: Section) => void;
  versDossier: boolean;
  partenaire: boolean;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [formation, setFormation] = useState('');

  // Les filtres ne proposent que ce que le dossier contient : une liste de
  // statuts jamais rencontrés ne ferait que des culs-de-sac.
  const statuts = useMemo(() => {
    const vus = new Map<string, string>();
    contrats.forEach((contrat) => vus.set(contrat.statut, contrat.statutLibelle));
    return [...vus.entries()];
  }, [contrats]);
  const formations = useMemo(() => {
    const vues = new Map<string, string>();
    contrats.forEach((contrat) => vues.set(contrat.formationCode, contrat.formation));
    return [...vues.entries()];
  }, [contrats]);

  const terme = normaliser(recherche.trim());
  const { affiches, total, pagination } = useListeFiltree(
    contrats,
    (contrat) =>
      (statut === '' || contrat.statut === statut) &&
      (formation === '' || contrat.formationCode === formation) &&
      (terme === '' ||
        normaliser(`${contrat.apprenant} ${contrat.formation} ${contrat.tuteur ?? ''}`).includes(
          terme,
        )),
    [terme, statut, formation],
  );

  return (
    <>
      <Onglets
        onglets={onglets}
        actif="contrats"
        onChanger={onSection}
        idPanneau="panneau-repertoire"
        actions={
          <>
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un contrat"
              placeholder="Apprenant, formation, tuteur"
            />
            <ChampFiltre valeur={statut} onChange={setStatut} etiquette="Filtrer par statut">
              <option value="">Tous les statuts</option>
              {statuts.map(([valeur, libelle]) => (
                <option key={valeur} value={valeur}>
                  {libelle}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre
              valeur={formation}
              onChange={setFormation}
              etiquette="Filtrer par formation"
            >
              <option value="">Toutes les formations</option>
              {formations.map(([code, libelle]) => (
                <option key={code} value={code}>
                  {libelle}
                </option>
              ))}
            </ChampFiltre>
          </>
        }
      />

      <PanneauOnglet id="panneau-repertoire" actif="contrats" className="space-y-5">
        <Carte
          titre="Contrats d’apprentissage"
          sansBordure
        >
          {partenaire ? (
            <Vide
              icone={<UserRound className="h-5 w-5" aria-hidden="true" />}
              texte="Un partenaire n’accueille pas d’apprenti : aucun contrat n’est rattaché à cette fiche."
            />
          ) : total === 0 ? (
            <Vide
              icone={<UserRound className="h-5 w-5" aria-hidden="true" />}
              texte={
                contrats.length === 0
                  ? 'Aucun apprenant sous contrat dans cette entreprise.'
                  : 'Aucun contrat ne correspond aux filtres actifs.'
              }
            />
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {['Apprenant', 'Formation', 'Période', 'Tuteur', 'Statut'].map((colonne) => (
                      <th
                        key={colonne}
                        className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                      >
                        {colonne}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {affiches.map((contrat) => (
                    <tr key={contrat.id} className="align-top transition hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-codialis-navy">
                        {versDossier ? (
                          <Link
                            to={`/adm-dossier/${contrat.apprenantId}`}
                            className="text-codialis-sky hover:underline"
                          >
                            {contrat.apprenant}
                          </Link>
                        ) : (
                          contrat.apprenant
                        )}
                        <div className="text-[11px] font-normal text-codialis-gray">
                          {contrat.typeLibelle}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">{contrat.formation}</td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {jour(contrat.dateDebut)} → {jour(contrat.dateFin)}
                        {contrat.finPeriodeEssai && (
                          <div className="text-[11px]">
                            Fin de période d’essai : {jour(contrat.finPeriodeEssai)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">
                        {contrat.tuteur ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <Pastille texte={contrat.statutLibelle} ton={contrat.statutTon} />
                        {contrat.rupture && (
                          <div className="mt-1 text-[11px] text-codialis-gray">
                            {mentionRupture(contrat.rupture.dateRupture)}
                            {contrat.rupture.motif && ` · ${contrat.rupture.motif}`}
                            {contrat.rupture.formationMaintenue && ' · formation maintenue'}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pagination}
        </Carte>
      </PanneauOnglet>
    </>
  );
}

function SectionImmersions({
  immersions,
  onglets,
  onSection,
  versDossier,
  partenaire,
  onVoirCompteRendu,
}: {
  immersions: ImmersionDossier[];
  onglets: Onglet<Section>[];
  onSection: (section: Section) => void;
  versDossier: boolean;
  partenaire: boolean;
  onVoirCompteRendu: (immersion: ImmersionDossier) => void;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');

  const statuts = useMemo(() => {
    const vus = new Map<string, string>();
    immersions.forEach((immersion) => vus.set(immersion.statut, immersion.statutLibelle));
    return [...vus.entries()];
  }, [immersions]);

  const terme = normaliser(recherche.trim());
  const { affiches, total, pagination } = useListeFiltree(
    immersions,
    (immersion) =>
      (statut === '' || immersion.statut === statut) &&
      (terme === '' ||
        normaliser(`${immersion.apprenant} ${immersion.formation ?? ''}`).includes(terme)),
    [terme, statut],
  );

  return (
    <>
      <Onglets
        onglets={onglets}
        actif="immersions"
        onChanger={onSection}
        idPanneau="panneau-repertoire"
        actions={
          <>
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher une immersion"
              placeholder="Apprenant, formation"
            />
            <ChampFiltre valeur={statut} onChange={setStatut} etiquette="Filtrer par statut">
              <option value="">Tous les statuts</option>
              {statuts.map(([valeur, libelle]) => (
                <option key={valeur} value={valeur}>
                  {libelle}
                </option>
              ))}
            </ChampFiltre>
          </>
        }
      />

      <PanneauOnglet id="panneau-repertoire" actif="immersions" className="space-y-5">
        <Carte
          titre="Périodes d’immersion en entreprise"
          sansBordure
        >
          {partenaire ? (
            <Vide
              icone={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
              texte="Une période d’immersion se déroule en entreprise : elle ne concerne pas cette fiche."
            />
          ) : total === 0 ? (
            <Vide
              icone={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
              texte={
                immersions.length === 0
                  ? 'Aucune période d’immersion enregistrée pour cette entreprise.'
                  : 'Aucune immersion ne correspond aux filtres actifs.'
              }
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {affiches.map((immersion) => (
                <li key={immersion.id} className="space-y-1 px-4 sm:px-6 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-codialis-navy">
                        {versDossier ? (
                          <Link
                            to={`/adm-dossier/${immersion.apprenantId}`}
                            className="text-codialis-sky hover:underline"
                          >
                            {immersion.apprenant}
                          </Link>
                        ) : (
                          immersion.apprenant
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-codialis-gray">
                        {periodePositionnement(immersion, jour)}
                        {immersion.formation ? ` · ${immersion.formation}` : ''}
                      </div>
                    </div>
                    <Pastille texte={immersion.statutLibelle} ton={immersion.statutTon} />
                  </div>
                  {immersion.avisEntreprise && (
                    <p className="whitespace-pre-line text-sm text-codialis-gray">
                      {immersion.avisEntreprise}
                    </p>
                  )}
                  {immersion.decideLe && (
                    <div className="text-[11px] text-codialis-gray">
                      Décision du {jour(immersion.decideLe)}
                    </div>
                  )}
                  {/* Le compte rendu d'entretien est le document du dossier
                      apprenant : la relation entreprise le consulte ici sans
                      qu'une copie soit produite pour l'entreprise. */}
                  {immersion.compteRendu ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <Icone nom="document" className="h-4 w-4 shrink-0 text-codialis-sky" />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-codialis-navy">
                            Compte rendu d’entretien en entreprise
                          </div>
                          <div className="text-[11px] text-codialis-gray">
                            Visite du {jour(immersion.compteRendu.dateVisite)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onVoirCompteRendu(immersion)}
                        className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
                      >
                        <Icone nom="oeil" className="h-3.5 w-3.5" />
                        Voir
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-codialis-gray">
                      Aucune évaluation réalisée.
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {pagination}
        </Carte>
      </PanneauOnglet>
    </>
  );
}

function SectionDocuments({
  documents,
  onglets,
  onSection,
  telechargeable,
  partenaire,
  onVoir,
}: {
  documents: DocumentDossierOrganisation[];
  onglets: Onglet<Section>[];
  onSection: (section: Section) => void;
  telechargeable: boolean;
  partenaire: boolean;
  /** Ouvre le document dans la visionneuse, sans quitter le dossier. */
  onVoir: (document: DocumentDossierOrganisation) => void;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [type, setType] = useState('');

  const types = useMemo(() => {
    const vus = new Map<string, string>();
    documents.forEach((document) => vus.set(document.type, document.intitule));
    return [...vus.entries()];
  }, [documents]);

  const terme = normaliser(recherche.trim());
  const { affiches, total, pagination } = useListeFiltree(
    documents,
    (document) =>
      (type === '' || document.type === type) &&
      (terme === '' ||
        normaliser(`${document.intitule} ${document.apprenant ?? ''}`).includes(terme)),
    [terme, type],
  );

  return (
    <>
      <Onglets
        onglets={onglets}
        actif="documents"
        onChanger={onSection}
        idPanneau="panneau-repertoire"
        actions={
          <>
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un document"
              placeholder="Intitulé, apprenant"
            />
            <ChampFiltre valeur={type} onChange={setType} etiquette="Filtrer par type">
              <option value="">Tous les types</option>
              {types.map(([valeur, libelle]) => (
                <option key={valeur} value={valeur}>
                  {libelle}
                </option>
              ))}
            </ChampFiltre>
          </>
        }
      />

      <PanneauOnglet id="panneau-repertoire" actif="documents" className="space-y-5">
        <Carte
          titre="Documents"
          sansBordure
        >
          {partenaire ? (
            <Vide
              icone={<FileText className="h-5 w-5" aria-hidden="true" />}
              texte="Les documents produits portent sur un contrat d’apprentissage : cette fiche n’en a pas."
            />
          ) : total === 0 ? (
            <Vide
              icone={<FileText className="h-5 w-5" aria-hidden="true" />}
              texte={
                documents.length === 0
                  ? 'Aucun document produit pour les contrats de cette entreprise.'
                  : 'Aucun document ne correspond aux filtres actifs.'
              }
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {affiches.map((document) => {
                // Une convention dit son état de validation par la direction (TF-430).
                const pastille = document.etatConvention ?? PASTILLE_DOCUMENT[document.statut];
                return (
                  <li
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <FileText className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-codialis-navy">
                          {document.intitule}
                        </div>
                        <div className="text-xs text-codialis-gray">
                          {document.apprenant ? `${document.apprenant} · ` : ''}
                          généré le {jour(document.genereLe)}
                          {document.transmisLe ? ` · transmis le ${jour(document.transmisLe)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {pastille && <Pastille texte={pastille.texte} ton={pastille.ton} />}
                      {/* Lire d'abord, emporter ensuite : « Voir » ouvre le
                          document sur place, le second bouton le télécharge.
                          Les deux passent par la route gardée. */}
                      {telechargeable && document.telechargeable && (
                        <>
                          <button
                            type="button"
                            onClick={() => onVoir(document)}
                            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-codialis-sky"
                            aria-label={`Voir ${document.intitule}`}
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                            Voir
                          </button>
                          <a
                            href={urlDocumentGenere(document.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                            aria-label={`Télécharger ${document.intitule}`}
                          >
                            <Download className="h-4 w-4" aria-hidden="true" />
                            Télécharger
                          </a>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {pagination}
          {!partenaire && (
            <div className="border-t border-gray-100 px-4 sm:px-6 py-4 text-[11px] text-codialis-gray">
              Les documents affichés sont ceux produits pour les contrats de cette entreprise. Ils se
              génèrent depuis le dossier de l’apprenant concerné : un document réglementaire porte sur
              une personne, pas sur l’organisation.
            </div>
          )}
        </Carte>
      </PanneauOnglet>
    </>
  );
}

/** Message d'absence : une section vide dit pourquoi elle l'est. */
function Vide({ icone, texte }: { icone: JSX.Element; texte: string }): JSX.Element {
  return (
    <div className="flex items-start gap-3 px-4 sm:px-6 py-10 text-sm text-codialis-gray">
      <span className="text-gray-300">{icone}</span>
      <p>{texte}</p>
    </div>
  );
}
